import {
  MoodTrackerEntriesResponse,
  MoodTrackerEntry,
  MoodTrackerStatsResponse,
  ProgressAssessmentStatsResponse,
  SliceStatus,
} from 'interfaces';
import {uniq} from 'ramda';

import {ActionReducerMapBuilder, createSlice} from '@reduxjs/toolkit';

import {
  createMoodTrackerEntry,
  createMoodTrackerEntryFailure,
  createMoodTrackerEntrySuccess,
  getAllProgressAssessmentStats,
  getAllProgressAssessmentStatsFailure,
  getAllProgressAssessmentStatsSuccess,
  getMoodTrackerEntries,
  getMoodTrackerEntriesFailure,
  getMoodTrackerEntriesSuccess,
  getMoodTrackerStats,
  getMoodTrackerStatsFailure,
  getMoodTrackerStatsSuccess,
  getProgressAssessmentStats,
  getProgressAssessmentStatsFailure,
  getProgressAssessmentStatsSuccess,
  updateProgressAssessment,
} from './progressActions';
import {getMoodTrackerStateKey, getProgressStateKey} from './utils';

type ProgressSliceState = {
  data: MoodTrackerEntriesResponse;
  status: SliceStatus;
  createStatus: SliceStatus;
  updateProgressAssessmentStatus: SliceStatus;
  moodTrackerStats: {
    [key: string]: {
      data: MoodTrackerStatsResponse;
      status: SliceStatus;
      isInitialized?: boolean;
    };
  };
  progressAssessmentStats: {
    [key: string]: {
      data: ProgressAssessmentStatsResponse;
      status: SliceStatus;
      isInitialized?: boolean;
    };
  };
  error: string | null;
};

const defaultMoodTrackerData = {
  entries: [],
  nextCursor: null,
};

const progressSlice = createSlice({
  name: 'memberProgress',
  initialState: {
    data: defaultMoodTrackerData,
    status: SliceStatus.idle,
    createStatus: SliceStatus.idle,
    moodTrackerStats: {},
    updateProgressAssessmentStatus: SliceStatus.idle,
    progressAssessmentStats: {},
    error: null,
  } as ProgressSliceState,
  reducers: {},
  extraReducers: (builder: ActionReducerMapBuilder<ProgressSliceState>) =>
    builder
      .addCase(getMoodTrackerEntries, (state, {payload}) => {
        if (payload?.reset) {
          // Direct property assignment instead of object spread
          state.data = defaultMoodTrackerData;
        }
        state.status = SliceStatus.pending;
        state.error = null;
      })
      .addCase(getMoodTrackerEntriesSuccess, (state, {payload}) => {
        state.status = SliceStatus.resolved;

        state.data.entries = uniq([...state.data.entries, ...payload.entries]);

        state.data.nextCursor = payload.nextCursor || null;
        state.error = null;
      })
      .addCase(getMoodTrackerEntriesFailure, (state, {payload}) => {
        state.status = SliceStatus.rejected;
        state.error = payload;
      })
      .addCase(getMoodTrackerStats, (state, {payload}) => {
        const key = getMoodTrackerStateKey(payload);
        if (!state.moodTrackerStats[key]) {
          state.moodTrackerStats[key] = {
            data: [],
            status: SliceStatus.pending,
          };
        } else {
          state.moodTrackerStats[key].status = SliceStatus.pending;
          state.moodTrackerStats[key].data = [];
        }
        state.error = null;
      })
      .addCase(getMoodTrackerStatsSuccess, (state, {payload}) => {
        const key = getMoodTrackerStateKey(payload);
        if (!state.moodTrackerStats[key]) {
          state.moodTrackerStats[key] = {
            data: payload.data,
            status: SliceStatus.resolved,
            isInitialized: true,
          };
        } else {
          Object.assign(state.moodTrackerStats[key], {
            data: payload.data,
            status: SliceStatus.resolved,
            isInitialized: true,
          });
        }
        state.error = null;
      })
      .addCase(getMoodTrackerStatsFailure, (state, {payload}) => {
        const key = getMoodTrackerStateKey(payload);
        if (!state.moodTrackerStats[key]) {
          state.moodTrackerStats[key] = {
            data: [],
            status: SliceStatus.rejected,
          };
        } else {
          Object.assign(state.moodTrackerStats[key], {
            data: [],
            status: SliceStatus.rejected,
          });
        }
        state.error = payload.error;
      })
      .addCase(createMoodTrackerEntry, (state, {payload}) => {
        // Create a new entry with direct property assignments instead of spread
        const newEntry = {} as MoodTrackerEntry;

        // Copy all properties from payload directly
        Object.keys(payload).forEach(key => {
          (newEntry as any)[key] = payload[key as keyof typeof payload];
        });

        // Add the createdAt timestamp
        newEntry.createdAt = new Date().toISOString();

        // Insert at the beginning of the array
        state.data.entries.unshift(newEntry);
        state.createStatus = SliceStatus.pending;
        state.error = null;
      })
      .addCase(createMoodTrackerEntryFailure, (state, {payload}) => {
        state.createStatus = SliceStatus.rejected;
        state.error = payload;
        if (state.data.entries.length > 0) {
          state.data.entries.shift();
        }
      })
      .addCase(createMoodTrackerEntrySuccess, (state, {payload}) => {
        // Update the matching entry
        for (let i = 0; i < state.data.entries.length; i++) {
          const entry = state.data.entries[i];
          if (entry._id === payload._id) {
            state.data.entries[i] = payload;
          }
        }
        state.createStatus = SliceStatus.resolved;
        state.error = null;
      })
      .addCase(getProgressAssessmentStats, (state, {payload}) => {
        const key = getProgressStateKey(payload);
        if (!state.progressAssessmentStats[key]) {
          Object.assign(state.progressAssessmentStats[key], {
            data: [],
            status: SliceStatus.pending,
          });
        }
        state.error = null;
      })
      .addCase(getProgressAssessmentStatsSuccess, (state, {payload}) => {
        const key = getProgressStateKey(payload);
        if (!state.progressAssessmentStats[key]) {
          state.progressAssessmentStats[key] = {
            data: payload.data,
            status: SliceStatus.resolved,
            isInitialized: true,
          };
        } else {
          Object.assign(state.progressAssessmentStats[key], {
            data: payload.data,
            status: SliceStatus.resolved,
            isInitialized: true,
          });
        }
        state.error = null;
      })
      .addCase(getProgressAssessmentStatsFailure, (state, {payload}) => {
        const key = getProgressStateKey(payload);
        if (!state.progressAssessmentStats[key]) {
          state.progressAssessmentStats[key] = {
            data: [],
            status: SliceStatus.rejected,
          };
        }
        state.error = payload.error;
      })
      .addCase(updateProgressAssessment.pending, state => {
        state.updateProgressAssessmentStatus = SliceStatus.pending;
        state.error = null;
      })
      .addCase(updateProgressAssessment.fulfilled, state => {
        state.updateProgressAssessmentStatus = SliceStatus.resolved;
        state.error = null;
      })
      .addCase(updateProgressAssessment.rejected, state => {
        state.updateProgressAssessmentStatus = SliceStatus.rejected;
        state.error = null;
      })
      .addCase(getAllProgressAssessmentStats, (state, {payload}) => {
        // Create keys using direct property assignments
        const gad7Payload = {...payload, type: 'gad7'};
        const phq9Payload = {...payload, type: 'phq9'};

        const gad7Key = getProgressStateKey(gad7Payload);
        const phq9Key = getProgressStateKey(phq9Payload);
        const moodTrackerKey = getMoodTrackerStateKey(payload);

        // Helper function to initialize or reset state
        const initializeStats = (
          stateObj: Record<string, any>,
          key: string,
        ) => {
          if (!stateObj[key]) {
            stateObj[key] = {
              data: [],
              status: SliceStatus.pending,
            };
          } else {
            stateObj[key].data = [];
            stateObj[key].status = SliceStatus.pending;
          }
        };

        // Initialize all stats objects
        initializeStats(state.progressAssessmentStats, gad7Key);
        initializeStats(state.progressAssessmentStats, phq9Key);
        initializeStats(state.moodTrackerStats, moodTrackerKey);

        state.error = null;
      })
      .addCase(getAllProgressAssessmentStatsSuccess, (state, {payload}) => {
        // Create keys using direct property assignments
        const gad7Payload = {...payload, type: 'gad7'};
        const phq9Payload = {...payload, type: 'phq9'};

        const gad7Key = getProgressStateKey(gad7Payload);
        const phq9Key = getProgressStateKey(phq9Payload);
        const moodTrackerKey = getMoodTrackerStateKey(payload);

        // Helper function to update stats with data
        const updateStatsWithData = (
          stateObj: Record<string, any>,
          key: string,
          data: any,
        ) => {
          if (!stateObj[key]) {
            stateObj[key] = {
              data: data,
              status: SliceStatus.resolved,
              isInitialized: true,
            };
          } else {
            stateObj[key].data = data;
            stateObj[key].status = SliceStatus.resolved;
            stateObj[key].isInitialized = true;
          }
        };

        // Update all stats objects with their respective data
        updateStatsWithData(
          state.progressAssessmentStats,
          gad7Key,
          payload.data.gad7,
        );
        updateStatsWithData(
          state.progressAssessmentStats,
          phq9Key,
          payload.data.phq9,
        );
        updateStatsWithData(
          state.moodTrackerStats,
          moodTrackerKey,
          payload.data.moodTracker,
        );

        state.error = null;
      })
      .addCase(getAllProgressAssessmentStatsFailure, (state, {payload}) => {
        // Create keys using direct property assignments
        const gad7Payload = {...payload, type: 'gad7'};
        const phq9Payload = {...payload, type: 'phq9'};

        const gad7Key = getProgressStateKey(gad7Payload);
        const phq9Key = getProgressStateKey(phq9Payload);
        const moodTrackerKey = getMoodTrackerStateKey(payload);

        // Helper function to reset stats on failure
        const resetStatsOnFailure = (
          stateObj: Record<string, any>,
          key: string,
        ) => {
          if (!stateObj[key]) {
            stateObj[key] = {
              data: [],
              status: SliceStatus.rejected,
            };
          } else {
            stateObj[key].data = [];
            stateObj[key].status = SliceStatus.rejected;
          }
        };

        // Reset all stats objects
        resetStatsOnFailure(state.progressAssessmentStats, gad7Key);
        resetStatsOnFailure(state.progressAssessmentStats, phq9Key);
        resetStatsOnFailure(state.moodTrackerStats, moodTrackerKey);

        state.error = payload.error;
      }),
});

export const {reducer: progressReducer, name: progressReducerName} =
  progressSlice;

export type TProgressActions =
  | ReturnType<typeof getMoodTrackerEntries>
  | ReturnType<typeof getMoodTrackerEntriesFailure>
  | ReturnType<typeof getMoodTrackerEntriesSuccess>
  | ReturnType<typeof getMoodTrackerStats>
  | ReturnType<typeof getMoodTrackerStatsFailure>
  | ReturnType<typeof getMoodTrackerStatsSuccess>
  | ReturnType<typeof getProgressAssessmentStats>
  | ReturnType<typeof getProgressAssessmentStatsFailure>
  | ReturnType<typeof getProgressAssessmentStatsSuccess>
  | ReturnType<typeof getAllProgressAssessmentStats>
  | ReturnType<typeof getAllProgressAssessmentStatsFailure>
  | ReturnType<typeof getAllProgressAssessmentStatsSuccess>
  | ReturnType<typeof createMoodTrackerEntry>
  | ReturnType<typeof createMoodTrackerEntryFailure>
  | ReturnType<typeof createMoodTrackerEntrySuccess>;

export const progressActions = {
  getMoodTrackerEntries,
  getMoodTrackerEntriesFailure,
  getMoodTrackerEntriesSuccess,
  getMoodTrackerStats,
  getMoodTrackerStatsFailure,
  getMoodTrackerStatsSuccess,
  getProgressAssessmentStats,
  getProgressAssessmentStatsFailure,
  getProgressAssessmentStatsSuccess,
  getAllProgressAssessmentStats,
  getAllProgressAssessmentStatsFailure,
  getAllProgressAssessmentStatsSuccess,
  updateProgressAssessment,
  createMoodTrackerEntry,
  createMoodTrackerEntryFailure,
  createMoodTrackerEntrySuccess,
};

export type ProgressState = ReturnType<typeof progressReducer>;
